// Spec 27 PR 2b — WorkspaceConfirm widget.
//
// The catalog exemplar. See spec 27 §9.6 for the full design.
//
// Anatomy:
//   1. Question (28px, centered, fades in first)
//   2. Workspace name (44px, click-to-edit in place, per-word reveal
//      at 150ms intervals when initialName is present)
//   3. Confidence ticks beneath the name (~40 ticks, lit ones
//      depend on confidence level)
//   4. Inferred-from caption (uppercase, dim, visible only while
//      the name is unedited and the inference exists)
//   5. Primary Pill ("Use this →" / "Create →"), labels driven by
//      props.confirmLabel
//   6. Secondary text link ("Use a different name")
//
// State machine visuals:
//   - idle: full layout, with reveal choreography on first mount.
//   - dirty: ticks dim entirely, pill label is the dirty one.
//   - submitting: pill shows spinner, whole widget at 80% opacity.
//   - succeeded: widget compresses to a single trace line.
//   - failed: warning underline + error caption, pill says "Try a
//     different name".

import { ArrowRight, Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Pill } from "@/components/glass";

import type { WidgetRenderProps } from "../registry";

interface WorkspaceConfirmProps {
	question: string;
	initialName: string;
	confidence: {
		level: "known" | "heuristic" | "none";
		sourceLabel: string | null;
	};
	confirmLabel: {
		idle: string;
		dirty: string;
	};
	placeholder?: string;
}

const TICK_COUNT = 40;
const WORD_REVEAL_INTERVAL_MS = 150;

export function WorkspaceConfirm({
	props,
	state,
	onAction,
}: WidgetRenderProps<WorkspaceConfirmProps>) {
	const [name, setName] = useState(props.initialName);
	const [edited, setEdited] = useState(false);
	const [revealedWords, setRevealedWords] = useState<number>(
		props.initialName === "" ? 0 : 0,
	);
	const inputRef = useRef<HTMLInputElement>(null);
	const [editing, setEditing] = useState(props.initialName === "");

	// Per-word reveal choreography on first mount when there's an
	// inferred name. Skipped if the user starts editing immediately.
	useEffect(() => {
		if (props.initialName === "") return;
		const words = props.initialName.split(/\s+/).filter(Boolean);
		if (words.length === 0) return;

		let cancelled = false;
		let i = 0;
		const tick = () => {
			if (cancelled) return;
			i++;
			setRevealedWords(i);
			if (i < words.length) {
				setTimeout(tick, WORD_REVEAL_INTERVAL_MS);
			}
		};
		const t = setTimeout(tick, WORD_REVEAL_INTERVAL_MS);
		return () => {
			cancelled = true;
			clearTimeout(t);
		};
	}, [props.initialName]);

	// Focus the input when entering editing mode.
	useEffect(() => {
		if (editing) {
			inputRef.current?.focus();
		}
	}, [editing]);

	// External state takeover: if the surface moves to submitting/
	// succeeded/failed, freeze the local UI.
	const submitting = state.kind === "submitting";
	const succeeded = state.kind === "succeeded";
	const failed = state.kind === "failed";

	// Trace state.
	if (succeeded) {
		const result = state.result as
			| { workspaceId?: string; name?: string }
			| undefined;
		const finalName = result?.name ?? name ?? props.initialName;
		return (
			<div className="text-center py-2">
				<span className="text-[12px] text-[var(--color-text-muted)]">
					<span className="text-[var(--color-success)] mr-1.5">✓</span>
					{finalName} created
				</span>
			</div>
		);
	}

	const inferredWords = props.initialName
		.split(/\s+/)
		.filter(Boolean);
	const displayName = edited
		? name
		: inferredWords.slice(0, revealedWords).join(" ");
	const ticksLit = computeTicksLit(props.confidence, inferredWords.length);
	const ticksVisible = props.confidence.level !== "none" && !edited;
	const pillLabel = edited ? props.confirmLabel.dirty : props.confirmLabel.idle;

	const handleEdit = (next: string) => {
		setName(next);
		if (!edited) {
			setEdited(true);
			onAction("dirty", { name: next });
		}
	};

	const startEditing = () => {
		// Snap reveal to completion + flip into editing mode.
		setRevealedWords(inferredWords.length);
		if (!edited && props.initialName !== "") {
			setName(props.initialName);
		}
		setEditing(true);
	};

	const submit = () => {
		if (submitting) return;
		const finalName = (edited ? name : props.initialName).trim();
		if (finalName.length === 0) {
			setEditing(true);
			inputRef.current?.focus();
			return;
		}
		onAction("confirm", { name: finalName });
	};

	return (
		<div
			className="text-center py-10 space-y-7"
			style={{ opacity: submitting ? 0.8 : 1, transition: "opacity 200ms" }}
		>
			{/* Question */}
			<div className="text-[28px] font-medium tracking-tight text-[var(--color-text)] anim-fade-rise">
				{props.question}
			</div>

			{/* Name (display or input) */}
			<div className="space-y-3">
				{editing ? (
					<input
						ref={inputRef}
						type="text"
						value={name}
						onChange={(e) => handleEdit(e.target.value)}
						onKeyDown={(e) => {
							if (e.key === "Enter") {
								e.preventDefault();
								submit();
							}
						}}
						placeholder={props.placeholder ?? "Workspace name"}
						className="block w-full max-w-[480px] mx-auto text-center text-[44px] font-medium tracking-tight bg-transparent border-0 outline-none text-[var(--color-text)] placeholder:text-[var(--color-text-dim)]"
						style={{
							borderBottom: failed
								? "1px solid var(--color-warning, #f59e0b)"
								: "1px solid transparent",
							transition: "border-color 200ms",
						}}
						disabled={submitting}
					/>
				) : (
					<button
						type="button"
						onClick={startEditing}
						className="block w-full max-w-[480px] mx-auto text-center text-[44px] font-medium tracking-tight text-[var(--color-text)] hover:opacity-90 cursor-text"
						style={{
							minHeight: "56px",
							borderBottom: failed
								? "1px solid var(--color-warning, #f59e0b)"
								: "1px solid transparent",
							transition: "border-color 200ms",
						}}
					>
						{displayName || (
							<span className="text-[var(--color-text-dim)]">
								{props.placeholder ?? "Workspace name"}
							</span>
						)}
					</button>
				)}

				{/* Confidence ticks */}
				{ticksVisible && (
					<div
						className="flex items-center justify-center gap-[3px] anim-fade-in"
						style={{ animationDelay: "200ms" }}
						aria-hidden="true"
					>
						{Array.from({ length: TICK_COUNT }).map((_, i) => (
							<span
								key={i}
								className="block w-px h-3"
								style={{
									background:
										i < ticksLit
											? "rgba(167,139,250,0.65)"
											: "rgba(255,255,255,0.08)",
									transition: "background-color 200ms",
								}}
							/>
						))}
					</div>
				)}

				{/* Inferred-from caption / failed caption */}
				{failed ? (
					<div className="text-[11px] text-[var(--color-warning,#f59e0b)]">
						{(state as { kind: "failed"; error: string }).error.startsWith(
							"db_error",
						)
							? "Couldn't save that. Try again?"
							: "That didn't work. Try a different name?"}
					</div>
				) : (
					ticksVisible &&
					props.confidence.sourceLabel && (
						<div
							className="text-[10px] uppercase tracking-wider text-[var(--color-text-dim)] anim-fade-in"
							style={{ animationDelay: "320ms" }}
						>
							{props.confidence.sourceLabel}
						</div>
					)
				)}
			</div>

			{/* Primary pill */}
			<div
				className="flex justify-center anim-fade-rise"
				style={{ animationDelay: "440ms" }}
			>
				<Pill
					onClick={submit}
					disabled={submitting || (edited && name.trim().length === 0)}
					className="!h-11 !px-5 !text-[14px]"
				>
					{submitting ? (
						<Loader2 className="w-4 h-4 animate-spin" />
					) : (
						<>
							{failed ? "Try a different name" : pillLabel}
							<ArrowRight className="w-4 h-4" />
						</>
					)}
				</Pill>
			</div>

			{/* Secondary link */}
			{!editing && !submitting && !failed && (
				<button
					type="button"
					onClick={startEditing}
					className="text-[12px] text-[var(--color-text-dim)] hover:text-[var(--color-text-muted)] transition-colors anim-fade-in"
					style={{ animationDelay: "560ms" }}
				>
					Use a different name
				</button>
			)}
		</div>
	);
}

function computeTicksLit(
	confidence: WorkspaceConfirmProps["confidence"],
	wordCount: number,
): number {
	if (confidence.level === "none") return 0;
	if (confidence.level === "known") return TICK_COUNT;
	// Heuristic: light up roughly one-third per detected word, capped.
	const lit = Math.min(TICK_COUNT, Math.max(8, wordCount * 12));
	return lit;
}
